import type { CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';
import type { NombreIcono } from './iconos';
import estilos from './Estados.module.css';

interface EstadoBaseProps {
  titulo: string;
  descripcion?: string;
  icono?: NombreIcono;
  color?: string;
  acciones?: ReactNode;
}

function EstadoBase({ titulo, descripcion, icono, color, acciones }: EstadoBaseProps) {
  return (
    <div className={estilos.contenedor} style={{ '--estado-color': color } as CSSProperties}>
      {icono && (
        <div className={estilos.icono}>
          <Icon nombre={icono} tamano={26} />
        </div>
      )}
      <div className={estilos.titulo}>{titulo}</div>
      {descripcion && <p className={estilos.descripcion}>{descripcion}</p>}
      {acciones && <div className={estilos.acciones}>{acciones}</div>}
    </div>
  );
}

/**
 * No hay datos que mostrar.
 * Siempre explica por qué está vacío y, si se puede, qué hacer al respecto.
 */
export function EstadoVacio({
  titulo = 'No hay nada que mostrar',
  descripcion,
  icono = 'vacio',
  acciones,
}: Partial<EstadoBaseProps>) {
  return (
    <EstadoBase
      titulo={titulo}
      descripcion={descripcion}
      icono={icono}
      color="var(--text-3)"
      acciones={acciones}
    />
  );
}

/** Todo correcto: no hay alarmas ni incidencias. Es una buena noticia, no un vacío. */
export function EstadoTodoBien({
  titulo = 'Todo en orden',
  descripcion = 'No hay alarmas activas en este momento.',
  acciones,
}: Partial<EstadoBaseProps>) {
  return (
    <EstadoBase
      titulo={titulo}
      descripcion={descripcion}
      icono="exito"
      color="var(--status-success)"
      acciones={acciones}
    />
  );
}

/**
 * Algo falló.
 * El mensaje debe decirle al usuario qué puede hacer; el detalle técnico va a
 * la consola, no a la pantalla.
 */
export function EstadoError({
  titulo = 'No se pudo cargar la información',
  descripcion = 'Revisa tu conexión e inténtalo de nuevo. Si el problema continúa, avisa al administrador.',
  acciones,
}: Partial<EstadoBaseProps>) {
  return (
    <EstadoBase
      titulo={titulo}
      descripcion={descripcion}
      icono="sinConexion"
      color="var(--status-danger)"
      acciones={acciones}
    />
  );
}

/** Sin permiso para ver esta sección. */
export function EstadoSinPermiso({
  titulo = 'No tienes acceso a esta sección',
  descripcion = 'Tu rol no incluye este módulo. Si crees que deberías verlo, contacta al administrador.',
  acciones,
}: Partial<EstadoBaseProps>) {
  return (
    <EstadoBase
      titulo={titulo}
      descripcion={descripcion}
      icono="candado"
      color="var(--status-warning)"
      acciones={acciones}
    />
  );
}

/** Bloque gris animado que ocupa el sitio del contenido mientras carga. */
export function Esqueleto({
  ancho = '100%',
  alto = 14,
  className,
}: {
  ancho?: string | number;
  alto?: string | number;
  className?: string;
}) {
  return (
    <div
      className={[estilos.esqueleto, className].filter(Boolean).join(' ')}
      style={{ width: ancho, height: alto }}
      aria-hidden
    />
  );
}

/**
 * Esqueleto de una lista.
 * Se prefiere a un girador porque conserva la forma de la pantalla y evita
 * que el contenido "salte" al llegar.
 */
export function ListaEsqueleto({ filas = 6 }: { filas?: number }) {
  return (
    <div className={estilos.listaEsqueletos} role="status" aria-label="Cargando">
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className={[estilos.esqueleto, estilos.tarjetaEsqueleto].join(' ')} />
      ))}
    </div>
  );
}

/** Indicador de carga para acciones puntuales dentro de una pantalla ya visible. */
export function Cargando({ etiqueta = 'Cargando' }: { etiqueta?: string }) {
  return (
    <div className={estilos.contenedor} role="status" aria-label={etiqueta}>
      <div className={estilos.girador} />
      <div className={estilos.descripcion}>{etiqueta}…</div>
    </div>
  );
}
